import type { CompleteDeviceVersionObject } from "../schemas/commonSubSchemas/completeDeviceVersion.js";
import {
    assertCompleteDeviceVersionIsWellFormed,
    COMPLETE_DEVICE_VERSION_SEGMENTS,
    ITEM_PATTERNS,
    isKnownValueWellFormed,
    SEGMENT_VALUE_REGEXES,
    type SegmentKind,
    termPattern,
    UNKNOWN_TOKEN,
    WILDCARD_TOKEN,
} from "./completeDeviceVersionGrammar.js";

const normalizeSegment: Record<SegmentKind, (segment: string) => string | number> = {
    numeric: (segment) => parseInt(segment),
    text: (segment) => segment.toUpperCase(),
};

function parseCompleteDeviceVersionSegment(kind: SegmentKind, segment: string): string | number | undefined {
    if (segment === UNKNOWN_TOKEN) {
        return undefined;
    }
    const invalidSegmentError = () => new Error(`Invalid ${kind} segment "${segment}" in complete device version`);
    if (!SEGMENT_VALUE_REGEXES[kind].test(segment)) {
        throw invalidSegmentError();
    }
    const value = normalizeSegment[kind](segment);
    if (!isKnownValueWellFormed[kind](value)) {
        throw invalidSegmentError();
    }
    return value;
}

/** Serializes in lowercase, e.g. "verdi.4.block.5.a.12". */
export function completeDeviceVersionObjectToString(completeDeviceVersion: CompleteDeviceVersionObject): string {
    assertCompleteDeviceVersionIsWellFormed(completeDeviceVersion);
    return COMPLETE_DEVICE_VERSION_SEGMENTS.map(({ key }) => {
        const value = completeDeviceVersion[key];
        return value === undefined ? UNKNOWN_TOKEN : String(value);
    })
        .join(".")
        .toLowerCase();
}

/** Parses any case into the stored object form: uppercase text, numbers, and absent unknown segments. */
export function completeDeviceVersionStringToObject(completeDeviceVersion: string): CompleteDeviceVersionObject {
    const segments = completeDeviceVersion.split(".");
    if (segments.length !== COMPLETE_DEVICE_VERSION_SEGMENTS.length) {
        throw new Error(
            `Complete device version "${completeDeviceVersion}" must have ${COMPLETE_DEVICE_VERSION_SEGMENTS.length} segments`,
        );
    }
    const parsed = Object.fromEntries(
        COMPLETE_DEVICE_VERSION_SEGMENTS.flatMap(({ key, kind }, i) => {
            const value = parseCompleteDeviceVersionSegment(kind, segments[i]);
            return value === undefined ? [] : [[key, value]];
        }),
    ) as CompleteDeviceVersionObject;
    assertCompleteDeviceVersionIsWellFormed(parsed);
    return parsed;
}

export type CompleteDeviceVersionSegmentKey = (typeof COMPLETE_DEVICE_VERSION_SEGMENTS)[number]["key"];

/**
 * Which segments of a version to write. The manufacturer is always written. Set a segment only when this report carries
 * it: `true` with the value omitted clears the stored segment.
 */
export type CompleteDeviceVersionSegmentsToSet = { manufacturer: true } & Record<
    Exclude<CompleteDeviceVersionSegmentKey, "manufacturer">,
    boolean
>;

export const ALL_COMPLETE_DEVICE_VERSION_SEGMENTS_SET: CompleteDeviceVersionSegmentsToSet = {
    manufacturer: true,
    generation: true,
    productLine: true,
    hardwareVersion: true,
    hardwareVariant: true,
    firmwareVersion: true,
};

/**
 * Throws unless `set` has a boolean for every segment and sets the manufacturer, the manufacturer is known, and every
 * set segment is absent (unknown) or well-formed. Values of unset segments are not checked.
 */
export function assertCompleteDeviceVersionSegmentsToSetAreWellFormed(
    version: CompleteDeviceVersionObject,
    set: CompleteDeviceVersionSegmentsToSet,
): void {
    if (typeof set !== "object" || set === null) {
        throw new Error("Complete device version segments to set must be an object");
    }
    COMPLETE_DEVICE_VERSION_SEGMENTS.forEach(({ key, kind }) => {
        if (typeof set[key] !== "boolean") {
            throw new Error(`Complete device version segments to set must have a boolean ${key}`);
        }
        const value: unknown = version?.[key];
        if (set[key] && value !== undefined && !isKnownValueWellFormed[kind](value)) {
            throw new Error(`Malformed ${key} "${value}" in complete device version`);
        }
    });
    if (set.manufacturer !== true) {
        throw new Error("Complete device version manufacturer must be set");
    }
    if (version?.manufacturer === undefined) {
        throw new Error("Complete device version manufacturer must be known");
    }
}

const normalizeKnownValue = (value: string | number | undefined) =>
    typeof value === "string" ? value.toUpperCase() : value;

/**
 * Applies the set segments of `version` onto `stored`, from manufacturer to firmware: a set segment takes the incoming
 * value (absent means unknown), an unset one keeps the stored value unless a set segment above it changed, e.g.
 * stored `verdi.4.block.5.a.12` with only hardwareVersion 6 set gives `verdi.4.block.6.?.?`. Compares in any case; text
 * comes back uppercase.
 */
export function applyCompleteDeviceVersionSegments(
    stored: CompleteDeviceVersionObject | undefined,
    version: CompleteDeviceVersionObject,
    set: CompleteDeviceVersionSegmentsToSet,
): CompleteDeviceVersionObject {
    assertCompleteDeviceVersionSegmentsToSetAreWellFormed(version, set);
    const firstChangeIndex = COMPLETE_DEVICE_VERSION_SEGMENTS.findIndex(
        ({ key }) => set[key] && normalizeKnownValue(version[key]) !== normalizeKnownValue(stored?.[key]),
    );
    return Object.fromEntries(
        COMPLETE_DEVICE_VERSION_SEGMENTS.flatMap(({ key }, i) => {
            const droppedBelowChange = firstChangeIndex !== -1 && i > firstChangeIndex;
            const value = set[key] ? version[key] : droppedBelowChange ? undefined : stored?.[key];
            return value === undefined ? [] : [[key, normalizeKnownValue(value)]];
        }),
    ) as CompleteDeviceVersionObject;
}

const BASIC_SELECTOR_PATTERN = COMPLETE_DEVICE_VERSION_SEGMENTS.map(({ kind }) =>
    termPattern(ITEM_PATTERNS[kind]),
).join("\\.");
const CONSTRAINT_PATTERN = `(?:! *)?\\( *${BASIC_SELECTOR_PATTERN} *\\)`;
const BARE_SELECTOR_REGEX = new RegExp(`^ *${BASIC_SELECTOR_PATTERN} *$`, "i");
const COMPLETE_SELECTOR_REGEX = new RegExp(
    `^ *(?:${BASIC_SELECTOR_PATTERN}|${CONSTRAINT_PATTERN}(?: *${CONSTRAINT_PATTERN})*) *$`,
    "i",
);
const CONSTRAINT_REGEX = /(?:(!) *)?\( *([^()]*?) *\)/g;
const NUMERIC_ITEM_REGEX = /^([0-9]+)(?:([+-])|:([0-9]+))?$/;

/**
 * Returns true if the selector is a bare six-term basic selector or a sequence of "(basic)" / "!(basic)" constraints.
 * Spaces are allowed only around list items, inside parentheses and between constraints. Case-insensitive.
 */
export function isCompleteDeviceVersionSelectorValid(selector: string): boolean {
    return COMPLETE_SELECTOR_REGEX.test(selector);
}

const doesKnownItemMatch: Record<SegmentKind, (item: string, value: string | number) => boolean> = {
    numeric: (item, value) => {
        const match = NUMERIC_ITEM_REGEX.exec(item);
        if (match === null || typeof value !== "number") {
            return false;
        }
        const [, bound, direction, upperBound] = match;
        if (direction === "+") {
            return value >= parseInt(bound);
        }
        if (direction === "-") {
            return value <= parseInt(bound);
        }
        if (upperBound !== undefined) {
            return parseInt(bound) <= value && value <= parseInt(upperBound);
        }
        return parseInt(bound) === value;
    },
    text: (item, value) => item.toLowerCase() === String(value).toLowerCase(),
};

function doesCompleteSelectorItemMatch(kind: SegmentKind, item: string, value: string | number | undefined): boolean {
    if (item === WILDCARD_TOKEN) {
        return true;
    }
    if (item === UNKNOWN_TOKEN) {
        return value === undefined;
    }
    return value !== undefined && doesKnownItemMatch[kind](item, value);
}

function doesCompleteDeviceVersionMatchBasicSelector(
    completeDeviceVersion: CompleteDeviceVersionObject,
    basicSelector: string,
): boolean {
    const terms = basicSelector.split(".");
    return COMPLETE_DEVICE_VERSION_SEGMENTS.every(({ key, kind }, i) =>
        terms[i]
            .replace(/[[\] ]/g, "")
            .split(",")
            .some((item) => doesCompleteSelectorItemMatch(kind, item, completeDeviceVersion[key])),
    );
}

function toWellFormedCompleteDeviceVersion(
    completeDeviceVersion: CompleteDeviceVersionObject | string,
): CompleteDeviceVersionObject {
    if (typeof completeDeviceVersion === "string") {
        return completeDeviceVersionStringToObject(completeDeviceVersion);
    }
    assertCompleteDeviceVersionIsWellFormed(completeDeviceVersion);
    return completeDeviceVersion;
}

/**
 * Returns true if at least one inclusion in the selector matches the complete device version and no exclusion does.
 * "*" matches known and unknown segments, "?" matches only unknown segments. Throws on an invalid selector.
 */
export function doesCompleteDeviceVersionMatchSelector(
    completeDeviceVersion: CompleteDeviceVersionObject | string,
    selector: string,
): boolean {
    if (!isCompleteDeviceVersionSelectorValid(selector)) {
        throw new Error(`Invalid complete device version selector: "${selector}"`);
    }
    const version = toWellFormedCompleteDeviceVersion(completeDeviceVersion);
    const constraints = BARE_SELECTOR_REGEX.test(selector)
        ? [{ excluded: false, basicSelector: selector.trim() }]
        : Array.from(selector.matchAll(CONSTRAINT_REGEX), ([, negation, basicSelector]) => ({
              excluded: negation === "!",
              basicSelector,
          }));
    const matchingConstraints = constraints.filter(({ basicSelector }) =>
        doesCompleteDeviceVersionMatchBasicSelector(version, basicSelector),
    );
    return (
        matchingConstraints.some(({ excluded }) => !excluded) && !matchingConstraints.some(({ excluded }) => excluded)
    );
}
